import React, { useEffect, useMemo, useState } from "react";
import { ThemeProvider } from "styled-components";

import UserInformation from "./UserInfoCard";
import RentabilityChart from "./RentabilityChart";
import Header from "../../components/Header";
import { useHistory } from "react-router-dom";
import { useMediaQuery } from "@mui/material";
import { useTheme as useStyledTheme } from "styled-components";
import { useAuth } from "../../hooks/auth";
import Footer from "../../components/Footer";
import Payments from "./Payments";
import NewOffers from "./NewOffers";
import AdvertisePropertyCTA from "./AdvertisePropertyCTA";
import ExclusiveOwnerServices from "./ExclusiveOwnerServices";
import UserInfoNoData from "./UserInfoNoData";
import VisitsSchedule from "./VisitsSchedule";
import PatrimonySection from "./PatrimonySection";
import PropertyAnalytics from "./PropertyAnalytics";
import PendingSignatures from "./PendingSignatures";
import RentedProperties from "./RentedProperties";
import AvailableProperties from "./AvailableProperties";
import OwnerPortfolio from "./OwnerPortfolio";
import emptyDataImg from "../../assets/pages/OwnerDashboard/mensagem-prancheta 1.png";
import { dashboardTheme } from "./theme/dashboardTheme";
import { useOwnerVisits } from "./hooks/useOwnerVisits";
import { useKenloPayments } from "./hooks/useKenloPayments";
import { useOwnerOffers } from "./hooks/useOwnerOffers";
import { usePropertyAnalytics } from "./hooks/usePropertyAnalytics";
import { usePendingSignatures } from "./hooks/usePendingSignatures";
import { useAvailableOwnerListings } from "./hooks/useAvailableOwnerListings";
import { useOwnerDashboardShell } from "./hooks/useOwnerDashboardShell";
import { DEFAULT_VISITS_PERIOD } from "./constants/dashboardPeriods";
import { IS_PROPERTY_ANALYTICS_ENABLED } from "./constants/propertyAnalytics";
import { buildDashboardPeriodFromMonth } from "./services/ownerPatrimonyService";
import { isOwnerRequestPending } from "./utils/isOwnerRequestPending";
import { mergeKenloPaymentsForFilter } from "./utils/mergeKenloPaymentsForFilter";
import { getOfferPropertyFilterOptions } from "./utils/getOfferPropertyFilterOptions";
import { buildRentedPropertyCatalog } from "./utils/buildRentedPropertyCatalog";
import {
  enrichKenloPropertyOptions,
  enrichPropertyIdFilterOptions,
} from "./utils/enrichPropertyFilterOptions";
import { hasOwnerScopedAccess } from "../../utils/canManageSubLogins";
import {
  ChartFullRow,
  PatrimonyRow,
  PropertyAnalyticsRow,
  PendingSignaturesRow,
  AdvertisePropertySection,
  OwnerExclusiveServicesSection,
  AvailablePropertiesRow,
  DashboardContainer,
  DashboardGrid,
  PortfolioRow,
  Main,
  NoDataGrid,
  NoDataImage,
  NoDataLeft,
  NoDataRight,
  NoDataText,
  NoDataTextContainer,
  NoDataTitle,
  OffersWrapper,
  PaymentsWrapper,
  RentedPropertiesRow,
  Section,
  UserInfoRow,
  VisitsOffersRow,
  VisitsWrapper,
} from "./styles";

export default function OwnerDashboard() {
  const { access_token, user } = useAuth();
  const styledTheme = useStyledTheme();
  const smDown = useMediaQuery(`(max-width: ${styledTheme.breakpoints.mobileSm})`);
  const mdDown = useMediaQuery(`(max-width: ${styledTheme.breakpoints.tabletLg})`);
  const lgDown = useMediaQuery(`(max-width: ${styledTheme.breakpoints.desktop})`);
  const isStacked = smDown || mdDown;
  const history = useHistory();

  const [visitsPeriodPreset, setVisitsPeriodPreset] = useState(DEFAULT_VISITS_PERIOD);
  const [visitsPropertyId, setVisitsPropertyId] = useState<number | null>(null);
  const [authChecked, setAuthChecked] = useState(false);

  const isOwnerView = hasOwnerScopedAccess(user);

  const ownerDashboardTheme = useMemo(
    () => ({
      ...styledTheme,
      dashboard: dashboardTheme,
    }),
    [styledTheme]
  );

  const { shell, status: shellStatus } = useOwnerDashboardShell({
    accessToken: access_token,
    enabled: isOwnerView,
    refreshKey: 0,
  });

  const isShellReady = shellStatus === "success";
  const isShellPending = isOwnerRequestPending(shellStatus, isOwnerView);
  const kenloEndpoints = shell?.kenlo_endpoints;

  const {
    summary: kenloSummary,
    payments: kenloPayments,
    filterPayments: kenloFilterPayments,
    propertyFilterOptions: kenloPropertyOptions,
    paymentsPagination,
    status: kenloStatus,
    query: kenloQuery,
    setChartPeriod,
    setChartMonths,
    setPaymentsPeriod,
    setKenloPropertyId,
    setPaymentsPage,
    retry: retryKenloPayments,
  } = useKenloPayments({
    accessToken: access_token,
    enabled: isOwnerView && isShellReady,
    endpoints: kenloEndpoints,
  });

  const isKenloPending = isOwnerRequestPending(kenloStatus, isOwnerView && isShellReady);

  const propertyAnalytics = usePropertyAnalytics({
    accessToken: access_token,
    enabled: IS_PROPERTY_ANALYTICS_ENABLED && isOwnerView && isShellReady,
    endpoints: kenloEndpoints,
  });

  const pendingSignatures = usePendingSignatures({
    accessToken: access_token,
    enabled: isOwnerView && isShellReady,
    endpoints: kenloEndpoints,
  });

  const availableListings = useAvailableOwnerListings({
    accessToken: access_token,
    enabled: isOwnerView && isShellReady,
  });

  const showPendingSignatures =
    isOwnerView && isShellReady && !pendingSignatures.isNotConfigured;

  const chartData = kenloSummary?.rent_received_by_date ?? [];
  const revenueInsights = kenloSummary?.revenue_insights;

  const patrimonyPayments = useMemo(
    () => mergeKenloPaymentsForFilter(kenloFilterPayments, kenloPayments),
    [kenloFilterPayments, kenloPayments]
  );

  const {
    offers,
    filters: offersFilters,
    pagination: offersPagination,
    status: offersStatus,
    query: offersQuery,
    setPeriodPreset: setOffersPeriodPreset,
    setPropertyId: setOffersPropertyId,
    setStatusFilter: setOffersStatusFilter,
    setPage: setOffersPage,
  } = useOwnerOffers({
    accessToken: access_token,
    enabled: isOwnerView && isShellReady,
    endpoints: kenloEndpoints,
    historyYears: kenloSummary?.history_years,
    fallbackOffers: shell?.offers ?? [],
  });

  const isOffersPending = isOwnerRequestPending(offersStatus, isOwnerView && isShellReady);

  const historyYears = kenloSummary?.history_years;

  const visitsQuery = useMemo(
    () => ({
      periodPreset: visitsPeriodPreset,
      propertyId: visitsPropertyId,
      historyYears: visitsPeriodPreset === "all_time" ? historyYears : undefined,
    }),
    [visitsPeriodPreset, visitsPropertyId, historyYears]
  );

  const {
    visitsData,
    status: visitsStatus,
    setPastPage,
    setUpcomingPage,
  } = useOwnerVisits({
    accessToken: access_token,
    enabled: isOwnerView && isShellReady,
    query: visitsQuery,
  });

  useEffect(() => {
    if (!user) {
      history.push("/login");
      return;
    }

    if (!hasOwnerScopedAccess(user)) {
      history.push("/tenant-dashboard");
      return;
    }

    setAuthChecked(true);
  }, [history, user]);

  const ownerDetails = {
    name: shell?.name ?? "",
    additionalData: shell?.additional_data ?? {
      rented_properties: 0,
      available_properties: 0,
      schedules: 0,
      time_to_rent: 0,
    },
  };

  const showDashboardError = shellStatus === "error";
  const showDashboard = authChecked && isOwnerView && !showDashboardError;

  const chartZoom = kenloQuery.chartZoom ?? 12;

  const rentedPropertyCatalog = useMemo(
    () =>
      buildRentedPropertyCatalog(
        shell?.additional_data?.rented_properties_details
      ),
    [shell?.additional_data?.rented_properties_details]
  );

  const enrichedKenloPropertyOptions = useMemo(
    () => enrichKenloPropertyOptions(kenloPropertyOptions, rentedPropertyCatalog),
    [kenloPropertyOptions, rentedPropertyCatalog]
  );

  const offerPropertyOptions = useMemo(
    () =>
      enrichPropertyIdFilterOptions(
        getOfferPropertyFilterOptions(visitsData),
        rentedPropertyCatalog
      ),
    [visitsData, rentedPropertyCatalog]
  );

  async function handlePaymentsMonthChange(monthValue: string) {
    const period = buildDashboardPeriodFromMonth(monthValue);
    await setPaymentsPeriod(period);
  }

  return (
    <ThemeProvider theme={ownerDashboardTheme}>
      <DashboardContainer>
        <Header hasBorder={!smDown} hasSearchBar hasOptionToSearch />
        <Main $loading={!authChecked} $hasData={showDashboard}>
          <Section>
            {showDashboard && (
              <DashboardGrid>
                <UserInfoRow>
                  <UserInformation
                    smDown={smDown}
                    mdDown={mdDown}
                    lgDown={lgDown}
                    isShellLoading={isShellPending}
                    isKenloLoading={isKenloPending}
                    kenloSummary={kenloSummary}
                    {...ownerDetails}
                  />
                </UserInfoRow>
                <PortfolioRow>
                  <OwnerPortfolio
                    accessToken={access_token}
                    enabled={isOwnerView && isShellReady}
                    smDown={smDown}
                  />
                </PortfolioRow>
                <ChartFullRow>
                  <RentabilityChart
                    smDown={smDown}
                    lgDown={lgDown}
                    chartData={chartData}
                    revenueInsights={revenueInsights}
                    chartPeriod={kenloQuery.chartPeriod}
                    chartZoom={chartZoom}
                    selectedKenloPropertyId={kenloQuery.kenloPropertyId ?? null}
                    propertyOptions={enrichedKenloPropertyOptions}
                    isLoading={isKenloPending}
                    isError={kenloStatus === "error"}
                    onRetry={retryKenloPayments}
                    onChartPeriodChange={setChartPeriod}
                    onChartZoomChange={setChartMonths}
                    onPropertyChange={setKenloPropertyId}
                  />
                </ChartFullRow>
                <PaymentsWrapper>
                  <Payments
                    paymentsList={kenloPayments}
                    paymentsPeriod={kenloQuery.paymentsPeriod}
                    paymentsPagination={paymentsPagination}
                    isLoading={isShellPending || isKenloPending}
                    isError={kenloStatus === "error"}
                    onRetry={retryKenloPayments}
                    smDown={smDown}
                    mdDown={mdDown}
                    lgDown={lgDown}
                    propertyOptions={enrichedKenloPropertyOptions}
                    selectedKenloPropertyId={kenloQuery.kenloPropertyId ?? null}
                    rentedPropertyCatalog={rentedPropertyCatalog}
                    onPropertyChange={setKenloPropertyId}
                    onPaymentsPeriodChange={handlePaymentsMonthChange}
                    onPaymentsPageChange={setPaymentsPage}
                  />
                </PaymentsWrapper>
                <RentedPropertiesRow>
                  <RentedProperties
                    properties={shell?.additional_data?.rented_properties_details}
                    smDown={smDown}
                    lgDown={lgDown}
                  />
                </RentedPropertiesRow>
                <AvailablePropertiesRow>
                  <AvailableProperties
                    listings={availableListings.listings}
                    status={availableListings.status}
                    smDown={smDown}
                    lgDown={lgDown}
                  />
                </AvailablePropertiesRow>
                <PatrimonyRow>
                  <PatrimonySection
                    accessToken={access_token}
                    enabled={isOwnerView && isShellReady}
                    endpoints={kenloEndpoints}
                    payments={patrimonyPayments}
                    propertyFilterOptions={enrichedKenloPropertyOptions}
                    smDown={smDown}
                    lgDown={lgDown}
                  />
                </PatrimonyRow>
                {IS_PROPERTY_ANALYTICS_ENABLED && (
                  <PropertyAnalyticsRow>
                    <PropertyAnalytics
                      smDown={smDown}
                      lgDown={lgDown}
                      data={propertyAnalytics.data}
                      status={propertyAnalytics.status}
                      periodPreset={propertyAnalytics.periodPreset}
                      propertyId={propertyAnalytics.propertyId}
                      propertyOptions={propertyAnalytics.propertyOptions}
                      onPeriodChange={propertyAnalytics.setPeriodPreset}
                      onPropertyChange={propertyAnalytics.setPropertyId}
                      onRetry={propertyAnalytics.retry}
                    />
                  </PropertyAnalyticsRow>
                )}
                {showPendingSignatures && (
                  <PendingSignaturesRow>
                    <PendingSignatures
                      smDown={smDown}
                      lgDown={lgDown}
                      data={pendingSignatures.data}
                      status={pendingSignatures.status}
                      statusFilter={pendingSignatures.statusFilter}
                      pagination={pendingSignatures.data?.pagination ?? null}
                      isForbidden={pendingSignatures.isForbidden}
                      successMessage={pendingSignatures.successMessage}
                      onStatusFilterChange={pendingSignatures.setStatusFilter}
                      onPageChange={pendingSignatures.setPage}
                      onRetry={pendingSignatures.retry}
                      onRefreshList={pendingSignatures.refreshList}
                      onSigned={pendingSignatures.markSignedAndRefresh}
                      onDismissSuccessMessage={pendingSignatures.clearSuccessMessage}
                      rentedPropertyCatalog={rentedPropertyCatalog}
                    />
                  </PendingSignaturesRow>
                )}
                <VisitsOffersRow>
                  <VisitsWrapper>
                    <VisitsSchedule
                      visitsData={visitsData}
                      status={visitsStatus}
                      visitsPeriodPreset={visitsPeriodPreset}
                      selectedPropertyId={visitsPropertyId}
                      schedulesFallback={shell?.additional_data?.schedules}
                      smDown={smDown}
                      lgDown={lgDown}
                      onPeriodChange={setVisitsPeriodPreset}
                      onPropertyChange={setVisitsPropertyId}
                      onPastPageChange={setPastPage}
                      onUpcomingPageChange={setUpcomingPage}
                      rentedPropertyCatalog={rentedPropertyCatalog}
                    />
                  </VisitsWrapper>
                  <OffersWrapper>
                    <NewOffers
                      smDown={smDown}
                      lgDown={lgDown}
                      offersList={offers}
                      pagination={offersPagination}
                      periodPreset={offersQuery.periodPreset}
                      statusFilter={offersQuery.statusFilter ?? "pending"}
                      selectedPropertyId={offersQuery.propertyId ?? null}
                      propertyOptions={offerPropertyOptions}
                      periodRange={offersFilters?.period_range ?? null}
                      isLoading={isOffersPending}
                      onPeriodChange={setOffersPeriodPreset}
                      onStatusFilterChange={setOffersStatusFilter}
                      onPropertyChange={setOffersPropertyId}
                      onPageChange={setOffersPage}
                      rentedPropertyCatalog={rentedPropertyCatalog}
                    />
                  </OffersWrapper>
                </VisitsOffersRow>
                <OwnerExclusiveServicesSection>
                  <ExclusiveOwnerServices smDown={smDown} lgDown={lgDown} />
                </OwnerExclusiveServicesSection>
                <AdvertisePropertySection>
                  <AdvertisePropertyCTA smDown={smDown} />
                </AdvertisePropertySection>
              </DashboardGrid>
            )}
            {authChecked && showDashboardError && (
              <NoDataGrid>
                <NoDataLeft>
                  <UserInfoNoData
                    smDown={smDown}
                    lgDown={lgDown}
                    width="100%"
                    boxSizing="border-box"
                  />
                </NoDataLeft>
                <NoDataRight>
                  {isStacked && <NoDataTitle>Seus Rendimentos</NoDataTitle>}
                  <NoDataImage $stacked={isStacked} src={emptyDataImg} alt="" />
                  <NoDataTextContainer>
                    {!isStacked && <NoDataTitle>Seus Rendimentos</NoDataTitle>}
                    <NoDataText>
                      Não foi possível carregar o dashboard. Tente novamente em instantes.
                    </NoDataText>
                  </NoDataTextContainer>
                </NoDataRight>
              </NoDataGrid>
            )}
          </Section>
        </Main>
        <Footer variant="dark" />
      </DashboardContainer>
    </ThemeProvider>
  );
}
